import * as yup from "yup";

import { UserRole } from "../users";

const getRolesWhitelist = () => Object.values(UserRole);

const jwtPayloadSchema = yup.object({
    exp: yup.number().required(),
    iat: yup.number().required(),
    locale: yup.string().required(),
    roles: yup
        .array()
        // Filter out any unknown roles (ex: ROLE_DEMO)
        .transform(value => value.filter((role: any) => getRolesWhitelist().includes(role)))
        .of(yup.string().oneOf(getRolesWhitelist()).required())
        .required()
        .min(1),
    sub: yup.string().uuid().required(),
    username: yup.string().email().required(),
});

export interface JwtDecodeOptions {
    header?: boolean;
}

const jwtHeaderSchema = yup.object({
    typ: yup.string(),
    alg: yup.string(),
    kid: yup.string(),
});

export interface JwtHeader {
    typ?: string;
    alg?: string;
    kid?: string;
}

export type JwtPayload = yup.InferType<typeof jwtPayloadSchema>;

export class InvalidTokenError extends Error {}

InvalidTokenError.prototype.name = "InvalidTokenError";

function b64DecodeUnicode(str: string) {
    return decodeURIComponent(
        atob(str).replace(/(.)/g, (m, p) => {
            let code = (p as string).charCodeAt(0).toString(16).toUpperCase();
            if (code.length < 2) {
                code = "0" + code;
            }
            return "%" + code;
        }),
    );
}

function base64UrlDecode(str: string) {
    let output = str.replace(/-/g, "+").replace(/_/g, "/");
    switch (output.length % 4) {
        case 0:
            break;
        case 2:
            output += "==";
            break;
        case 3:
            output += "=";
            break;
        default:
            throw new Error("base64 string is not of the correct length");
    }

    try {
        return b64DecodeUnicode(output);
    } catch {
        return atob(output);
    }
}

export function jwtDecode(token: string, options: JwtDecodeOptions & { header: true }): JwtHeader;
export function jwtDecode(token: string, options?: JwtDecodeOptions): JwtPayload;
export function jwtDecode(token: string, options?: JwtDecodeOptions): JwtHeader | JwtPayload {
    if (typeof token !== "string") {
        throw new InvalidTokenError("Invalid token specified: must be a string");
    }

    options ||= {};

    const pos = options.header === true ? 0 : 1;
    const part = token.split(".")[pos];

    if (typeof part !== "string") {
        throw new InvalidTokenError(`Invalid token specified: missing part #${pos + 1}`);
    }

    let decoded: string;
    try {
        decoded = base64UrlDecode(part);
    } catch (e) {
        throw new InvalidTokenError(
            `Invalid token specified: invalid base64 for part #${pos + 1} (${(e as Error).message})`,
        );
    }

    try {
        const payload = JSON.parse(decoded);

        if (options.header === true) {
            return jwtHeaderSchema.validateSync(payload);
        }

        return jwtPayloadSchema.validateSync(payload);
    } catch (e) {
        throw new InvalidTokenError(
            `Invalid token specified: invalid json for part #${pos + 1} (${(e as Error).message})`,
        );
    }
}
